import { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Cpu } from "lucide-react";
import { blogPosts, categories, type Category } from "../data/blogPosts";
import { SEO } from "../components/SEO";

export function Blog() {
  const [activeCategory, setActiveCategory] = useState<Category>("All");

  const filteredPosts =
    activeCategory === "All"
      ? blogPosts
      : blogPosts.filter((p) => p.category === activeCategory);

  return (
    <>
      <SEO
        title="Blog | StarSinger MCP"
        description="Developer guides, comparisons and industry analysis for building with AI music via the Model Context Protocol."
        pathname="/blog"
      />
      <div className="min-h-[70vh]">
      {/* Header */}
      <section className="py-16 px-6 border-b border-border">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-400 text-sm font-medium mb-4">
            <Cpu className="w-4 h-4" />
            Use Cases & Guides
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            StarSinger MCP for Every Industry
          </h1>
          <p className="text-xl text-text-muted max-w-2xl mx-auto">
            Developer guides, comparisons and industry analysis for building with
            AI-curated music via the Model Context Protocol.
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="pt-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 flex-wrap mb-8">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30"
                    : "bg-surface border border-border text-text-muted hover:text-text hover:border-indigo-500/20"
                }`}
              >
                {cat}
                {cat !== "All" && (
                  <span className="ml-1.5 text-xs opacity-60">
                    ({blogPosts.filter((p) => p.category === cat).length})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-8">
            {activeCategory === "All" ? "All Articles" : activeCategory}
            <span className="text-text-muted text-lg font-normal ml-2">
              ({filteredPosts.length})
            </span>
          </h2>
          <div className="space-y-6">
            {filteredPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group block p-6 bg-surface border border-border rounded-xl hover:border-indigo-500/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:from-indigo-500/30 group-hover:to-purple-500/30 transition-all">
                    <post.icon className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-xs font-medium text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">
                        {post.category}
                      </span>
                      <span className="text-xs text-text-muted flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.date}
                      </span>
                      <span className="text-xs text-text-muted flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-indigo-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-text-muted mb-3">{post.excerpt}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs text-text-muted bg-surface-light px-2 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-text-muted group-hover:text-indigo-400 group-hover:translate-x-1 transition-all flex-shrink-0 hidden sm:block" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 border-t border-border">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">
            Ready to add AI music to your product?
          </h2>
          <p className="text-text-muted mb-2">
            Free tier: 100 requests/day. No credit card required.
          </p>
          <p className="text-sm text-text-muted mb-8">
            Install in 30 seconds: <code className="text-indigo-400">npx -y starsinger-mcp</code>
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-medium transition-all shadow-lg shadow-indigo-500/25"
            >
              Get Free API Key
            </Link>
            <a
              href="https://github.com/jupiterspins/starsinger-mcp"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-border hover:border-indigo-500/50 text-text px-8 py-3 rounded-xl font-medium transition-all"
            >
              View on GitHub
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
